/**
 * @krisspy-file
 * @type page
 * @name "IssuesPage"
 * @title "Issues"
 * @description "Global issues list with filters, sorting, and expandable details"
 * @routes ["/issues"]
 * @layout "DashboardLayout"
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, Search, Filter, ChevronDown, ChevronUp,
  Plus, Loader2, MapPin, ArrowUpDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  getIssues,
  type IssueWithRelations,
  type IssueFilters,
} from '../services/issues.service';
import { getLocations } from '../services/locations.service';
import { getUsers, type UserWithRole } from '../services/users.service';
import { supabase } from '../lib/supabase';
import ReportIssueModal from '../components/ReportIssueModal';
import { useDateFormatter } from '../hooks/useDateFormatter';

const STATUS_CONFIG: Record<string, { labelKey: string; color: string; bg: string }> = {
  reported: { labelKey: 'issues.statusReported', color: 'text-warning', bg: 'bg-warning/10' },
  confirmed: { labelKey: 'issues.statusConfirmed', color: 'text-blue', bg: 'bg-blue/10' },
  dismissed: { labelKey: 'issues.statusDismissed', color: 'text-purple-600', bg: 'bg-purple-100' },
  repair_assigned: { labelKey: 'issues.statusRepairAssigned', color: 'text-danger', bg: 'bg-danger/10' },
  resolved: { labelKey: 'issues.statusResolved', color: 'text-success', bg: 'bg-success/10' },
  cannot_repair: { labelKey: 'issues.statusCannotRepair', color: 'text-success', bg: 'bg-success/10' },
  closed: { labelKey: 'issues.statusClosed', color: 'text-gray-700', bg: 'bg-gray-200' },
};

const SEVERITY_CONFIG: Record<string, { labelKey: string; color: string }> = {
  low: { labelKey: 'issues.severityLow', color: 'text-secondary' },
  medium: { labelKey: 'issues.severityMedium', color: 'text-warning' },
  high: { labelKey: 'issues.severityHigh', color: 'text-danger' },
  critical: { labelKey: 'issues.severityCritical', color: 'text-danger font-bold' },
};

const SOURCE_LABEL_KEYS: Record<string, string> = {
  internal: 'issues.internal',
  customer_return: 'issues.customer',
  system: 'issues.system',
};

// Open statuses for default filter
const OPEN_STATUSES = ['reported', 'confirmed', 'repair_assigned'];

type SortField = 'created_at' | 'severity' | 'status' | 'title';
type SortDir = 'asc' | 'desc';

export default function IssuesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { merchantId, accessControl } = useAuth();
  const { showToast } = useToast();
  const { formatDateShort } = useDateFormatter();

  const [issues, setIssues] = useState<IssueWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Locations for filter dropdown
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);

  // Team members for assignee filter
  const [teamMembers, setTeamMembers] = useState<UserWithRole[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string[]>(OPEN_STATUSES);
  const [severityFilter, setSeverityFilter] = useState<string[]>([]);
  const [locationFilter, setLocationFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState(''); // '' = all, '__me__' = my issues, or assignee name
  const [searchQuery, setSearchQuery] = useState('');

  // Sorting - default: newest first
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    loadLocations();
    loadTeamMembers();
    loadUserId();
  }, []);

  useEffect(() => {
    loadIssues();
  }, [merchantId, statusFilter, severityFilter, locationFilter, sourceFilter, assigneeFilter, searchQuery]);

  async function loadLocations() {
    const { data } = await getLocations(
      accessControl,
      { active: true }
    );
    if (data) {
      setLocations(data.map(l => ({ id: l.id, name: l.location_name })));
    }
  }

  async function loadTeamMembers() {
    const { data } = await getUsers(accessControl);
    if (data) setTeamMembers(data);
  }

  async function loadUserId() {
    const { data } = await supabase.auth.getUser();
    setUserId(data?.user?.id || null);
  }

  async function loadIssues() {
    if (!merchantId) return;
    setLoading(true);
    const filters: IssueFilters = {};
    if (statusFilter.length > 0) filters.status = statusFilter;
    if (severityFilter.length > 0) filters.severity = severityFilter;
    if (locationFilter) filters.locationId = locationFilter;
    if (sourceFilter) filters.source = sourceFilter;
    if (assigneeFilter === '__me__' && userId) {
      filters.assigneeUserId = userId;
    } else if (assigneeFilter && assigneeFilter !== '__me__') {
      filters.assigneeName = assigneeFilter;
    }
    if (searchQuery.trim()) filters.searchQuery = searchQuery.trim();

    const { data, error } = await getIssues(merchantId, filters);
    if (error) {
      showToast('Failed to load issues: ' + error, 'error');
    } else {
      setIssues(data || []);
    }
    setLoading(false);
  }

  // Client-side sorting
  const sortedIssues = [...issues].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'created_at') {
      return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * dir;
    }
    if (sortField === 'severity') {
      const order = { critical: 4, high: 3, medium: 2, low: 1 };
      return ((order[a.severity] || 0) - (order[b.severity] || 0)) * dir;
    }
    if (sortField === 'status') {
      return a.status.localeCompare(b.status) * dir;
    }
    if (sortField === 'title') {
      return a.title.localeCompare(b.title) * dir;
    }
    return 0;
  });

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'created_at' ? 'desc' : 'asc');
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-secondary" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3" />
      : <ChevronDown className="w-3 h-3" />;
  }

  const toggleStatus = (status: string) => {
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  return (
    <div className="space-y-lg animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary">{t('issues.title')}</h1>
          <p className="text-sm text-secondary">{t('issues.description')}</p>
        </div>
        <button
          onClick={() => setShowReportModal(true)}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4" />
          {t('issues.reportIssue')}
        </button>
      </div>

      {/* Search & Filter Bar */}
      <div className="card p-md space-y-md">
        <div className="flex flex-col sm:flex-row gap-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('issues.searchPlaceholder')}
              className="input w-full pl-10"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn btn-ghost ${showFilters ? 'bg-blue/10 text-blue' : ''}`}
          >
            <Filter className="w-4 h-4" />
            {t('common.filter')}
            {(statusFilter.length !== OPEN_STATUSES.length || severityFilter.length > 0 || locationFilter || sourceFilter || assigneeFilter) && (
              <span className="w-2 h-2 rounded-full bg-blue" />
            )}
          </button>
        </div>

        {/* Status chips (always visible) */}
        <div className="flex flex-wrap gap-xs">
          {Object.entries(STATUS_CONFIG).map(([key, conf]) => (
            <button
              key={key}
              onClick={() => toggleStatus(key)}
              className={`px-sm py-xs rounded-full text-xs font-medium border transition-colors ${
                statusFilter.includes(key)
                  ? `${conf.bg} ${conf.color} border-current`
                  : 'bg-secondary/5 text-secondary border-transparent hover:border-secondary'
              }`}
            >
              {t(conf.labelKey)}
            </button>
          ))}
        </div>

        {/* Extended filters */}
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-md pt-md border-t border-border">
            <div>
              <label className="text-xs text-secondary mb-xs block">{t('issues.locationLabel')}</label>
              <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} className="input w-full text-sm">
                <option value="">{t('issues.allLocations')}</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-secondary mb-xs block">{t('issues.assignedTo')}</label>
              <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)} className="input w-full text-sm">
                <option value="">{t('issues.allAssignees')}</option>
                <option value="__me__">{t('issues.myIssues')}</option>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.full_name || ''}>
                    {m.full_name || t('issues.noName')}{m.role ? ` — ${m.role}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-secondary mb-xs block">{t('issues.sourceLabel')}</label>
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="input w-full text-sm">
                <option value="">{t('issues.allSources')}</option>
                <option value="internal">{t('issues.internal')}</option>
                <option value="customer_return">{t('issues.customerReturn')}</option>
                <option value="system">{t('issues.system')}</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-secondary mb-xs block">{t('issues.severityLabel')}</label>
              <div className="flex flex-wrap gap-xs">
                {Object.entries(SEVERITY_CONFIG).map(([key, conf]) => (
                  <button
                    key={key}
                    onClick={() => setSeverityFilter(prev =>
                      prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
                    )}
                    className={`px-sm py-xs rounded-full text-xs font-medium border transition-colors ${
                      severityFilter.includes(key)
                        ? `${conf.color} border-current bg-current/5`
                        : 'text-secondary border-transparent hover:border-secondary'
                    }`}
                  >
                    {t(conf.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Issues Table */}
      {loading ? (
        <div className="flex items-center justify-center py-2xl">
          <Loader2 className="w-8 h-8 animate-spin text-blue" />
        </div>
      ) : sortedIssues.length === 0 ? (
        <div className="card text-center py-2xl">
          <AlertTriangle className="w-12 h-12 text-secondary mx-auto mb-md" />
          <h3 className="text-lg font-semibold text-primary mb-xs">{t('issues.noIssuesFound')}</h3>
          <p className="text-sm text-secondary">
            {statusFilter.length > 0 || searchQuery ? t('issues.tryAdjusting') : t('issues.noIssuesReported')}
          </p>
        </div>
      ) : (
        <>
        {/* Mobile card layout */}
        <div className="sm:hidden space-y-sm">
          {sortedIssues.map((issue) => {
            const sc = STATUS_CONFIG[issue.status] || STATUS_CONFIG.reported;
            const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.medium;

            return (
              <div
                key={issue.id}
                className="card p-md cursor-pointer active:bg-secondary transition-colors"
                onClick={() => navigate(`/issues/${issue.id}`)}
              >
                <div className="flex items-start justify-between gap-sm">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-primary truncate">{issue.title}</p>
                    {(issue.inventory_items || issue.product_types) && (
                      <p className="text-xs text-secondary truncate mt-xs">
                        {issue.inventory_items?.item_number && <span>{issue.inventory_items.item_number}</span>}
                        {issue.inventory_items?.item_number && issue.product_types && <span> · </span>}
                        {issue.product_types && <span>{issue.product_types.product_name}</span>}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-secondary whitespace-nowrap flex-shrink-0">{formatDateShort(issue.created_at)}</span>
                </div>
                <div className="flex items-center gap-sm mt-sm flex-wrap">
                  <span className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                    {t(sc.labelKey)}
                  </span>
                  <span className={`text-xs font-medium ${sev.color}`}>{t(sev.labelKey)}</span>
                  {issue.assignee_name && (
                    <span className="text-xs text-secondary">→ {issue.assignee_name}</span>
                  )}
                </div>
              </div>
            );
          })}
          <div className="px-sm py-xs">
            <p className="text-xs text-secondary">{t('issues.issueCount', { count: sortedIssues.length })}</p>
          </div>
        </div>

        {/* Desktop/tablet table layout */}
        <div className="hidden sm:block card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b border-primary">
                <tr>
                  <th className="text-left py-md px-lg text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:text-primary" onClick={() => toggleSort('title')}>
                    <span className="flex items-center gap-xs">{t('issues.issue')} <SortIcon field="title" /></span>
                  </th>
                  <th className="text-left py-md px-lg text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:text-primary" onClick={() => toggleSort('status')}>
                    <span className="flex items-center gap-xs">{t('issues.status')} <SortIcon field="status" /></span>
                  </th>
                  <th className="text-left py-md px-lg text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:text-primary" onClick={() => toggleSort('severity')}>
                    <span className="flex items-center gap-xs">{t('issues.severity')} <SortIcon field="severity" /></span>
                  </th>
                  <th className="text-left py-md px-lg text-xs font-semibold text-secondary uppercase tracking-wider hidden md:table-cell">
                    {t('issues.item')}
                  </th>
                  <th className="text-left py-md px-lg text-xs font-semibold text-secondary uppercase tracking-wider hidden lg:table-cell">
                    {t('issues.location')}
                  </th>
                  <th className="text-left py-md px-lg text-xs font-semibold text-secondary uppercase tracking-wider">
                    {t('issues.source')}
                  </th>
                  <th className="text-left py-md px-lg text-xs font-semibold text-secondary uppercase tracking-wider">
                    {t('issues.assigned')}
                  </th>
                  <th className="text-left py-md px-lg text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:text-primary" onClick={() => toggleSort('created_at')}>
                    <span className="flex items-center gap-xs">{t('issues.created')} <SortIcon field="created_at" /></span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedIssues.map((issue) => {
                  const sc = STATUS_CONFIG[issue.status] || STATUS_CONFIG.reported;
                  const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.medium;

                  return (
                    <tr
                      key={issue.id}
                      className="hover:bg-secondary cursor-pointer transition-colors"
                      onClick={() => navigate(`/issues/${issue.id}`)}
                    >
                      <td className="py-md px-lg">
                        <div className="min-w-0">
                          <div className="flex items-center gap-sm">
                            <p className="text-sm font-medium text-primary truncate max-w-[300px]">
                              {issue.title}
                            </p>
                            {issue.inventory_items?.item_number && (
                              <span className="inline-flex items-center px-sm py-0.5 rounded text-xs font-medium bg-secondary/10 text-secondary whitespace-nowrap">
                                {issue.inventory_items.item_number}
                              </span>
                            )}
                          </div>
                          {issue.product_types && (
                            <p className="text-xs text-secondary truncate">{issue.product_types.product_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-md px-lg">
                        <span className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                          {t(sc.labelKey)}
                        </span>
                      </td>
                      <td className="py-md px-lg">
                        <span className={`text-xs font-medium ${sev.color}`}>{t(sev.labelKey)}</span>
                      </td>
                      <td className="py-md px-lg hidden md:table-cell">
                        <span className="text-sm text-secondary">
                          {issue.inventory_items?.item_number || '-'}
                        </span>
                      </td>
                      <td className="py-md px-lg hidden lg:table-cell">
                        {issue.locations ? (
                          <span className="text-sm text-secondary flex items-center gap-xs">
                            <MapPin className="w-3 h-3" />
                            {issue.locations.location_name}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-md px-lg">
                        <span className="text-xs text-secondary">{SOURCE_LABEL_KEYS[issue.source] ? t(SOURCE_LABEL_KEYS[issue.source]) : issue.source}</span>
                      </td>
                      <td className="py-md px-lg">
                        <span className="text-sm text-secondary">{issue.assignee_name || '-'}</span>
                      </td>
                      <td className="py-md px-lg">
                        <span className="text-xs text-secondary whitespace-nowrap">{formatDateShort(issue.created_at)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-lg py-md border-t border-border">
            <p className="text-xs text-secondary">{t('issues.issueCount', { count: sortedIssues.length })}</p>
          </div>
        </div>
        </>
      )}

      {/* Report Issue Modal */}
      <ReportIssueModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onIssueCreated={() => loadIssues()}
        initialData={{
          merchant_id: merchantId || '',
        }}
      />
    </div>
  );
}
